import React from 'react';
import routerStore from 'stores/router/RouterStore';
import currentUserStore from 'stores/CurrentUserStore';
import { observer } from 'mobx-react';
import retry from './../../../utils/helpers/retry.js';
import { mergeCustomPrograms } from './../../../utils/program-config/customPrograms.js';
import { requirements, verifier, redirects } from 'utils/route-authorization/';
import { PRIVATE_SANDBOX } from '../../../views/admin/uam-granular-permissions/constants.js';
import { SANDBOX } from '../../environment-selector/constants';
import { useProgramSelectorStateContext } from '../../program-selector/context.js';

const NotAllowedView = React.lazy(() =>
  retry(() => import('../../../views/not-allowed/NotAllowedView'))
);

const CurrentRoute = () => {
  const {
    currentRouteName,
    currentProgramShortCodeInUrl,
    currentEnvironmentInUrl,
    routeMap,
    route,
  } = routerStore;
  const { id: currentRouteId } = route || {};
  const { userStore = {} } = currentUserStore ? currentUserStore : {};
  const { programsList = [], activeProgram } = userStore;
  const hasPrivateSandboxRole = userStore.redseaRoles?.includes(PRIVATE_SANDBOX);

  // Merge custom qa programs data with programs for non-prod for development & testing.
  // these programs are not available in pp_table must be manually added (UPS-16614).
  const mergedProgramsList = mergeCustomPrograms(programsList);

  const userHasAccessToProgram = mergedProgramsList.find(
    ({ short_name }) => short_name === currentProgramShortCodeInUrl
  );

  // Use the ProgramSelector context's authorized programs to resolve the default
  // program for the "Go to default program" button. In the Auth0 flow,
  // userStore.activeProgram may still point to the unauthorized program (from
  // localStorage), so we prefer the first entry from the authorized list instead.
  const { programs: authorizedPrograms } = useProgramSelectorStateContext() || {};
  const defaultProgram =
    (authorizedPrograms &&
      authorizedPrograms.find(({ short_name }) => short_name !== currentProgramShortCodeInUrl)) ||
    activeProgram;

  if (currentProgramShortCodeInUrl && !userHasAccessToProgram && activeProgram) {
    return (
      <React.Suspense fallback={<div />}>
        <NotAllowedView
          desiredProgram={currentProgramShortCodeInUrl}
          defaultProgram={defaultProgram}
        />
      </React.Suspense>
    );
  }

  if (currentEnvironmentInUrl && currentEnvironmentInUrl === SANDBOX && !hasPrivateSandboxRole) {
    return (
      <React.Suspense fallback={<div />}>
        <NotAllowedView protectionRules={{ requiredRoles: [PRIVATE_SANDBOX] }} />
      </React.Suspense>
    );
  }

  const redirectTo = redirects(currentRouteId);

  if (redirectTo) {
    const validatedRedirectPath = routeMap && routeMap[redirectTo]?.path;
    if (validatedRedirectPath) {
      routerStore.go(validatedRedirectPath);
    } else {
      routerStore.go('/home');
    }
  }

  const viewVerified = verifier(currentRouteName);
  const viewIsProtected = requirements(currentRouteName);

  if (!viewVerified && viewIsProtected) {
    return (
      <React.Suspense fallback={<div />}>
        <NotAllowedView protectionRules={requirements(currentRouteName)} />
      </React.Suspense>
    );
  } else {
    const { currentView: View } = routerStore;
    return View && <View />;
  }
};

export default observer(CurrentRoute);
